import axios from 'axios'

import { Get } from 'config/apiServiceConfig'
import AxiosInstance from 'config/apiServiceApiGateway'
import { TagType } from 'features/Enterprise/ReorderList/types/rlFilter'

const getVendorsEndpoint = '/api/vendor'

const baseUrlFileUpload = process.env.REACT_APP_BASE_URL_API_FILE_SERVICE

export type VendorType = {
  id: number
  companyName: string
}

export type GetVendorsType = {
  data: VendorType[]
}

export type GetSlotListType = {
  data: {
    slot: number
    label?: string
  }[]
}

export type AvailableTimeSlotType = {
  id: number
  location_id: number
  loading_dock_id: number
  name: string
  start_at: string
  end_at: string
  manpower: number
  manpower_productivity: number
  timeslot_productivity: number
  is_active: true
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string
  remarks: string
  label?: string
  type: 'FCFS_PARENT' | 'FCFS_CHILD' | 'FCFS_PARENT_CHILD'
  register_start_at: string
  register_end_at: string
}

export type GetAvailableTimeSlotType = AvailableTimeSlotType[]

export type GetAvailableTimeSlotParamsType = {
  date: string
  loading_dock_id: number
  slot: number
}

export type PurchaseOrderDataType = {
  attribute: { is_tax_per_item: boolean; is_xdock: boolean }
  created_at: number
  created_by: string
  destination_address: string
  destination_id: number
  destination_name: string
  destination_contact_phone: string
  discount: number
  expiry_date: number
  histories: {
    activity_id: number
    activity_name: string
    duration: number
    end_date: number
    full_name: string
    start_date: number
  }[]
  id: number
  items: {
    actual_quantity: number
    bad_quantity: number
    business_taggings: {
      id: number
      key: string
      type: string
      value: string
    }[]
    buy_price: number
    discount: number
    discounted_price: number
    good_quantity: number
    id: number
    incoming_quantity: number
    item_details: {
      actual_quantity: number
      expiry_date: number
      id: number
      manage_exp: boolean
      note: string
      request_quantity: number
      status_id: number
      status_name: string
    }[]
    koli: number
    lost_quantity: number
    manage_exp: boolean
    note: string
    price: string
    product_age: number
    product_id: number
    product_name: string
    product_sku: string
    product_stock: number
    rack: string
    rack_id: number
    rack_position: number
    request_quantity: number
    subtotal_tax_price: number
    tax_rate: string
    total: number
    uom: string
  }[]
  netsuite_id: number
  note: string
  payment_term: string
  po_ref_number: string
  purchase_order_number: string
  ref_number: string
  request_by: string
  request_shipping_date: number
  shipping_cost: number
  status: string
  sub_total: number
  tax: number
  tax_rate: string
  total: number
  updated_at: number
  updated_by: string
  vendor_contact: string
  vendor_id: number
  vendor_name: string
  schedule_type: string
}

export type PoNumberType = {
  purchaseOrderId: number
  purchaseOrderNumber: string
  totalPcs: number
  hasStored?: boolean
  reason: string
  id: number
  vendorName?: string
  isVip?: boolean
}

export type KongPoNumberType = {
  id: number
  purchase_order_id: number
  purchase_order_number: string
  reason: string
  total_pcs: number
  hasStored?: boolean
  email_sent_at: string
  email_sent_by: string
  email_sent_state: string
}

export type GetPoNumberType = {
  data: PoNumberType[]
}

export type GetPoNumberParamsType = {
  destinationId: number
}

export type PostInboundScheduleDataType = {
  location_id: string
  loading_dock_id: string
  vendor_id: string
  date: string
  slot: number
  time_slot_ids: number[]
  purchase_orders: {
    id: string
    reason: string
  }[]
  executed_by: string
  user_id: string
}

export type DeletePoNumberDataType = {
  purchaseOrderId: number
}

export type PutInboundScheduleDataType = Pick<
  PostInboundScheduleDataType,
  'date' | 'slot' | 'time_slot_ids' | 'purchase_orders' | 'executed_by' | 'user_id'
>

export type GetInboundScheduleDetailType = {
  schedule_id: number
  vendor: {
    vendor_id: number
    vendor_name: string
  }
  date: string
  slot: number
  created_at: string
  created_by: string
  time_slot: AvailableTimeSlotType
  purchase_orders: {
    id: number
    purchase_order_id: number
    purchase_order_number: string
    total_pcs: number
    created_at?: string
    created_by?: string
    hasStored?: boolean
    reason: string
    email_sent_at: string
    email_sent_by: string
    email_sent_state: string
    vendor_name?: string
    booking_type?: 'VIP' | 'REGULAR' | 'FCFS'
    is_vip?: boolean
    tags?: TagType[]
  }[]
  max_pcs: number
  is_same_day: boolean
  time_slot_ids: number[]
  loading_dock_id: number
  register_start_at: string
  register_end_at: string
}

export type GetAuditTrailsDataType = {
  data: {
    purchase_order_id: string
    purchase_order_number: string
    action: string
    reason: string
    executed_by: string
    executed_at: string
  }[]
  pagination: {
    prev_cursor: string
    next_cursor: string
  }
}

export type GetReasonType = 'INBOUND_SCHEDULE_PO_CREATE' | 'INBOUND_SCHEDULE_PO_DELETE'

export type GetReasonResponseType = {
  id: number
  reason: string
}

export type PODetailsType = {
  created_by: string
  destination_address: string
  destination_name: string
  purchase_order_number: string
}

export const getVendors = (companyName: string) =>
  Get<GetVendorsType>({ url: getVendorsEndpoint, params: { companyName } })

export const getPoNumber = (params: GetPoNumberParamsType) =>
  AxiosInstance.Get<GetPoNumberType, true>({
    url: '/commercial/internal/:version/purchase-orders/unscheduled',
    version: 'v1',
    params,
    convertRequest: true,
    convertResponse: true,
  })

export const getAvailableTimeSlot = (params: GetAvailableTimeSlotParamsType) =>
  AxiosInstance.Get<GetAvailableTimeSlotType>({
    url: '/commercial/internal/:version/inbound-schedules/available-slots',
    version: 'v1',
    params,
  })

export const getSlotList = () =>
  AxiosInstance.Get<GetSlotListType>({
    url: '/commercial/internal/:version/inbound-schedules/slots',
    version: 'v1',
  })

export const postInboundSchedule = (data: PostInboundScheduleDataType) =>
  AxiosInstance.Post({
    url: '/commercial/internal/:version/inbound-schedules',
    data,
    version: 'v1',
  })

export const putInboundSchedule = (scheduleId: string, data: PutInboundScheduleDataType) =>
  AxiosInstance.Put({
    url: '/commercial/internal/v1/inbound-schedules/:scheduleId',
    version: 'v1',
    urlParams: { scheduleId },
    data,
  })

export const getInboundScheduleDetail = (scheduleId: string) =>
  AxiosInstance.Get<{ data: GetInboundScheduleDetailType }, true>({
    url: '/commercial/internal/:version/inbound-schedules/:scheduleId',
    version: 'v1',
    urlParams: { scheduleId },
  })

export const getAuditTrails = (inbound_schedule_id: string, cursor: string) =>
  AxiosInstance.Get<GetAuditTrailsDataType>({
    url: '/commercial/internal/:version/inbound-schedules/audit-trails',
    version: 'v1',
    params: {
      cursor,
      inbound_schedule_id,
      page_size: 10,
    },
  })

export const deletePoNumber = (
  scheduleId: string,
  purchaseOrderId: number,
  data: { executed_by: string; user_id: number; reason: string },
) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/inbound-schedules/:scheduleId/purchase-orders/:purchaseOrderId/delete',
    version: 'v1',
    urlParams: { scheduleId, purchaseOrderId },
    data,
  })

export const getReasons = (type: GetReasonType) =>
  AxiosInstance.Get<GetReasonResponseType[]>({
    url: '/commercial/internal/:version/reason/:type',
    version: 'v1',
    urlParams: { type },
  })

export const postPoDetailsEmail = (
  schedule_id: string,
  data: { purchase_order_id: number; pdf_url: string },
) =>
  AxiosInstance.Post<{ message: string }, true>({
    url: `/commercial/internal/:version/inbound-schedules/${schedule_id}/purchase-orders/${data.purchase_order_id}/send-email`,
    version: 'v1',
    data,
  })

export const postUploadFile = (data: FormData) =>
  axios.post<{ data: { fileUrl: string } }>(
    `${baseUrlFileUpload}/internal/v1/staff-management-inbound-schedule-po-email`,
    data,
    {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'multipart/form-data',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Service-Name': 'astro-app-web-admin',
      },
    },
  )

export const getCommercialFeatureFlag = () =>
  AxiosInstance.Get<{ data: { [key: string]: string } }, true>({
    url: '/commercial/internal/:version/feature-flags',
    version: 'v1',
  })
